import { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Divider,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Computer as ComputerIcon,
  Storage as StorageIcon,
  Language as WebsiteIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Shield as ShieldIcon,
  Cloud as CloudIcon,
  Apps as AppsIcon,
  Dns as DnsIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { StatusChip } from '../../../../../components/shared';
import { DeviceTableProps, Device, getCardBorder } from '../types';

type SortKey = 'displayName' | 'client' | 'type' | 'host' | 'status' | 'alertCount' | 'lastSeen';
type SortDirection = 'asc' | 'desc';

const STATUS_ORDER: Record<string, number> = { down: 0, warning: 1, up: 2 };

function parseLastSeen(lastSeen: string): number {
  if (lastSeen === 'Never') return Infinity;
  if (lastSeen === 'Just now') return 0;
  const match = lastSeen.match(/^(\d+)\s*(min|h|d)/);
  if (!match) return Infinity;
  const value = parseInt(match[1], 10);
  const unit = match[2];
  if (unit === 'min') return value;
  if (unit === 'h') return value * 60;
  if (unit === 'd') return value * 1440;
  return Infinity;
}

function comparator(a: Device, b: Device, sortKey: SortKey, direction: SortDirection): number {
  let cmp = 0;
  switch (sortKey) {
    case 'displayName':
      cmp = a.displayName.localeCompare(b.displayName);
      break;
    case 'client':
      cmp = a.client.localeCompare(b.client);
      break;
    case 'type':
      cmp = a.type.localeCompare(b.type);
      break;
    case 'host':
      cmp = a.host.localeCompare(b.host);
      break;
    case 'status':
      cmp = (STATUS_ORDER[a.status] ?? 2) - (STATUS_ORDER[b.status] ?? 2);
      break;
    case 'alertCount':
      cmp = a.alertCount - b.alertCount;
      break;
    case 'lastSeen':
      cmp = parseLastSeen(a.lastSeen) - parseLastSeen(b.lastSeen);
      break;
  }
  return direction === 'asc' ? cmp : -cmp;
}

/**
 * Maps DB category names to MUI icons.
 */
function DeviceTypeIcon({
  type,
  fontSize = 'small',
}: {
  type: string;
  fontSize?: 'small' | 'medium';
}) {
  switch (type) {
    case 'Infrastructure':
      return <ComputerIcon fontSize={fontSize} />;
    case 'Database':
      return <StorageIcon fontSize={fontSize} />;
    case 'Network':
      return <DnsIcon fontSize={fontSize} />;
    case 'Security':
      return <ShieldIcon fontSize={fontSize} />;
    case 'Virtualization':
      return <CloudIcon fontSize={fontSize} />;
    case 'Application':
      return <AppsIcon fontSize={fontSize} />;
    case 'Web':
      return <WebsiteIcon fontSize={fontSize} />;
    default:
      return <ComputerIcon fontSize={fontSize} />;
  }
}

/**
 * DeviceTable - Main device table with filters.
 *
 * Features:
 * - Filter chips for client, type, and status
 * - Clickable rows to open device detail drawer
 * - Status-based row highlighting (down = red tint, warning = yellow tint)
 * - Alert count badges
 * - Action buttons (manage alerts, more options)
 */
export function DeviceTable({
  devices,
  filters,
  clients,
  onFilterChange,
  onDeviceClick,
  loading = false,
  sx,
}: DeviceTableProps) {
  const theme = useTheme();
  // Use 'md' breakpoint (< 900px) so tablets also get card view instead of overflowing table
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [sortKey, setSortKey] = useState<SortKey>('displayName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortedDevices = useMemo(
    () => [...devices].sort((a, b) => comparator(a, b, sortKey, sortDirection)),
    [devices, sortKey, sortDirection]
  );

  const handleClientFilter = (clientId: number | 'all') => {
    onFilterChange({ clientId });
  };

  const handleTypeFilter = (type: string | 'all') => {
    const newType = filters.type === type ? 'all' : type;
    onFilterChange({ type: newType });
  };

  // Derive unique category types from the full device list for dynamic filter chips
  const categoryTypes = useMemo(() => {
    const types = new Set(devices.map((d) => d.type));
    // Sort with a stable order: known categories first, then alphabetical
    const order = ['Infrastructure', 'Database', 'Network', 'Security', 'Virtualization', 'Application', 'Web'];
    return Array.from(types).sort((a, b) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [devices]);

  const handleStatusFilter = (status: 'down' | 'warning' | 'all') => {
    const newStatus = filters.status === status ? 'all' : status;
    onFilterChange({ status: newStatus });
  };

  const getRowBgColor = (device: Device) => {
    if (device.status === 'down') {
      return alpha(theme.palette.error.main, 0.04);
    }
    if (device.status === 'warning') {
      return alpha(theme.palette.warning.main, 0.04);
    }
    return 'inherit';
  };

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: getCardBorder(theme),
          overflow: 'hidden',
          ...sx,
        }}
      >
        {/* Header Skeleton */}
        <Box
          sx={{
            p: 2,
            borderBottom: getCardBorder(theme),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton animation="wave" variant="text" width={80} height={28} />
            <Skeleton animation="wave" variant="rounded" width={60} height={24} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} animation="wave" variant="rounded" width={80} height={24} />
            ))}
          </Box>
        </Box>

        {/* Table Skeleton */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                {['Device', 'Client', 'Type', 'Host', 'Status', 'Alerts', 'Last Seen'].map(
                  (header) => (
                    <TableCell key={header}>
                      <Skeleton animation="wave" width={60} />
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 8 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Skeleton animation="wave" variant="circular" width={32} height={32} />
                      <Box>
                        <Skeleton animation="wave" width={120} height={18} />
                        <Skeleton animation="wave" width={80} height={14} />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" width={80} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" variant="rounded" width={60} height={22} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" width={100} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" variant="rounded" width={50} height={22} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" variant="rounded" width={70} height={22} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" width={60} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  // Get first 3 clients for quick filter chips
  const quickFilterClients = clients.slice(0, 3);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: getCardBorder(theme),
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Filters Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: getCardBorder(theme),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" fontWeight="bold">
            Devices
          </Typography>
          <Chip label={`${devices.length} shown`} size="small" variant="outlined" />
          <TextField
            size="small"
            placeholder="Search devices..."
            value={filters.search || ''}
            onChange={(e) => onFilterChange({ search: e.target.value })}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: filters.search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => onFilterChange({ search: '' })} aria-label="Clear search">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
            sx={{ minWidth: 200 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {/* Client Filter */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label="All Clients"
              size="small"
              variant={filters.clientId === 'all' ? 'filled' : 'outlined'}
              aria-pressed={filters.clientId === 'all'}
              onClick={() => handleClientFilter('all')}
              sx={{ cursor: 'pointer' }}
            />
            {quickFilterClients.map((c) => (
              <Chip
                key={c.id}
                label={c.shortName}
                size="small"
                variant={filters.clientId === c.id ? 'filled' : 'outlined'}
                color={filters.clientId === c.id ? 'primary' : 'default'}
                aria-pressed={filters.clientId === c.id}
                onClick={() => handleClientFilter(c.id)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

          {/* Type Filter — dynamic from DB categories */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {categoryTypes.map((cat) => (
              <Chip
                key={cat}
                icon={<DeviceTypeIcon type={cat} fontSize="small" />}
                label={cat}
                size="small"
                variant={filters.type === cat ? 'filled' : 'outlined'}
                color={filters.type === cat ? 'primary' : 'default'}
                aria-pressed={filters.type === cat}
                onClick={() => handleTypeFilter(cat)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

          {/* Status Filter */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label="Down"
              size="small"
              color="error"
              variant={filters.status === 'down' ? 'filled' : 'outlined'}
              aria-pressed={filters.status === 'down'}
              onClick={() => handleStatusFilter('down')}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="Warning"
              size="small"
              color="warning"
              variant={filters.status === 'warning' ? 'filled' : 'outlined'}
              aria-pressed={filters.status === 'warning'}
              onClick={() => handleStatusFilter('warning')}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Box>
      </Box>

      {/* Mobile Card View / Desktop Table View */}
      {isMobile ? (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {devices.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No devices match your filters</Typography>
              </Box>
            ) : (
              sortedDevices.map((device) => (
                <Paper
                  key={device.id}
                  elevation={0}
                  role="button"
                  tabIndex={0}
                  onClick={() => onDeviceClick(device)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onDeviceClick(device);
                    }
                  }}
                  sx={{
                    p: 2,
                    border: getCardBorder(theme),
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: getRowBgColor(device),
                    '&:hover': { borderColor: theme.palette.primary.main },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Avatar
                      sx={{
                        width: 32, height: 32,
                        bgcolor: device.status === 'up' ? alpha(theme.palette.success.main, 0.1)
                          : device.status === 'down' ? alpha(theme.palette.error.main, 0.1)
                          : alpha(theme.palette.warning.main, 0.1),
                        color: device.status === 'up' ? 'success.main' : device.status === 'down' ? 'error.main' : 'warning.main',
                      }}
                    >
                      <DeviceTypeIcon type={device.type} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">{device.displayName}</Typography>
                      <Typography variant="caption" color="text.secondary">{device.name}</Typography>
                    </Box>
                    <StatusChip status={device.status} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">Client: {device.client}</Typography>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">{device.host}</Typography>
                    {device.alertCount > 0 && (
                      <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label={device.alertCount} size="small" color="error" variant="outlined" />
                    )}
                    <Typography variant="caption" color="text.secondary">{device.lastSeen}</Typography>
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                  {([
                    { key: 'displayName', label: 'Device' },
                    { key: 'client', label: 'Client' },
                    { key: 'type', label: 'Type' },
                    { key: 'host', label: 'Host' },
                    { key: 'status', label: 'Status' },
                    { key: 'alertCount', label: 'Alerts' },
                    { key: 'lastSeen', label: 'Last Seen' },
                  ] as { key: SortKey; label: string }[]).map((col) => (
                    <TableCell key={col.key} sortDirection={sortKey === col.key ? sortDirection : false}>
                      <TableSortLabel
                        active={sortKey === col.key}
                        direction={sortKey === col.key ? sortDirection : 'asc'}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}

                </TableRow>
              </TableHead>
              <TableBody>
                {sortedDevices.map((device) => (
                  <TableRow
                    key={device.id}
                    hover
                    tabIndex={0}
                    onClick={() => onDeviceClick(device)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onDeviceClick(device);
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: getRowBgColor(device),
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor:
                              device.status === 'up'
                                ? alpha(theme.palette.success.main, 0.1)
                                : device.status === 'down'
                                ? alpha(theme.palette.error.main, 0.1)
                                : alpha(theme.palette.warning.main, 0.1),
                            color:
                              device.status === 'up'
                                ? 'success.main'
                                : device.status === 'down'
                                ? 'error.main'
                                : 'warning.main',
                          }}
                        >
                          <DeviceTypeIcon type={device.type} />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {device.displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {device.name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={device.client}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={device.type}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                        {device.host}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={device.status} />
                    </TableCell>
                    <TableCell>
                      {device.alertCount > 0 ? (
                        <Chip
                          icon={<WarningIcon sx={{ fontSize: 14 }} />}
                          label={device.alertCount}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {device.lastSeen}
                        </Typography>
                        {device.collectionMethod === 'push' && (
                          <Chip label="push" size="small" variant="outlined" color="info"
                            sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Empty State */}
          {devices.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No devices match your filters</Typography>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}

export default DeviceTable;
